import React, { useRef, useEffect, useCallback, useState } from 'react';
import {
    BarcodeFormat,
    BinaryBitmap,
    DecodeHintType,
    HTMLCanvasElementLuminanceSource,
    HybridBinarizer,
    MultiFormatReader,
    NotFoundException,
} from '@zxing/library';
import { scanImageData, setModuleArgs, ZBarSymbolType, ZBarConfigType, ZBarScanner } from '@undecaf/zbar-wasm';

setModuleArgs({
    locateFile: () => {
        return '/zbar.wasm';
    },
});

interface CameraViewProps {
    cameraEnabled: boolean;
    onBarcodeDetected: (barcode: string) => void;
    cameraStreamRef: React.MutableRefObject<MediaStream | null>;
    autoConvertUpca: boolean;
    selectedDeviceId?: string;
    onCameraReady?: () => void;
}

interface ScanRegionPreset {
    widthRatio: number;
    heightRatio: number;
    centerYRatio?: number;
    centerXRatio?: number;
    outputScale?: number;
    maxVariants?: number;
}

interface ImageStats {
    min: number;
    max: number;
    average: number;
}

interface OverlayRect {
    x: number;
    y: number;
    width: number;
    height: number;
    strokeStyle: string;
    lineWidth: number;
}

interface TrackedRegion {
    x: number;
    y: number;
    width: number;
    height: number;
    expiresAt: number;
    score: number;
}

interface ScanSuccessInfo {
    code: string;
    decoder: 'zbar' | 'zxing';
    roiX: number;
    roiY: number;
    roiWidth: number;
    roiHeight: number;
    outputWidth: number;
    outputHeight: number;
    widthRatio: number | null;
    heightRatio: number | null;
    outputScale: number | null;
    variantIndex: number;
    tracked: boolean;
}

interface NumericMediaSettingsRange {
    min: number;
    max: number;
    step?: number;
}

interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
    focusMode?: string[];
    resizeMode?: string[];
    zoom?: NumericMediaSettingsRange;
    sharpness?: NumericMediaSettingsRange;
    width?: NumericMediaSettingsRange;
    height?: NumericMediaSettingsRange;
}

interface ExtendedMediaTrackConstraintSet extends MediaTrackConstraintSet {
    focusMode?: ConstrainDOMString;
    resizeMode?: ConstrainDOMString;
    zoom?: ConstrainDouble;
    sharpness?: ConstrainDouble;
    width?: ConstrainULong;
    height?: ConstrainULong;
}

interface ExtendedMediaTrackConstraints extends MediaTrackConstraints {
    advanced?: ExtendedMediaTrackConstraintSet[];
}

const SCAN_FPS = 6;
const SCAN_INTERVAL_MS = Math.floor(1000 / SCAN_FPS);

const CAMERA_BOOTSTRAP_WIDTH = 1920;
const CAMERA_BOOTSTRAP_HEIGHT = 1080;
const CAMERA_MAX_FRAME_RATE = 30;

const MAX_SCAN_WIDTH = 1600;
const MAX_SCAN_HEIGHT = 900;
const TRACKED_REGION_TTL_MS = 1400;
const DEBUG_UNLOCK_TAPS = 10;
const DEBUG_TAP_INTERVAL_MS = 1200;

const ZXING_FORMATS = [
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODABAR,
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.ITF,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
];

const SCAN_PROFILES: ScanRegionPreset[][] = [
    [{ widthRatio: 0.96, heightRatio: 0.26, outputScale: 1, maxVariants: 2 }],
    [{ widthRatio: 0.34, heightRatio: 0.96, outputScale: 1, maxVariants: 2 }],
    [{ widthRatio: 0.78, heightRatio: 0.78, outputScale: 1, maxVariants: 1 }],
];

const DUP_TTL_MS = 1000;

const toGrayscale = (imageData: ImageData) => {
    const data = imageData.data;
    let min = 255;
    let max = 0;
    let total = 0;

    for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const gray = (38 * r + 75 * g + 15 * b) >> 7;
        data[i] = data[i + 1] = data[i + 2] = gray;
        min = Math.min(min, gray);
        max = Math.max(max, gray);
        total += gray;
    }

    return {
        min,
        max,
        average: total / (data.length / 4),
    } satisfies ImageStats;
};

const cloneImageData = (imageData: ImageData) => {
    return new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
};

const stretchContrast = (imageData: ImageData, stats: ImageStats) => {
    const range = Math.max(1, stats.max - stats.min);
    if (range >= 180) {
        return imageData;
    }

    const data = imageData.data;
    const scale = 255 / range;

    for (let i = 0; i < data.length; i += 4) {
        const value = Math.max(0, Math.min(255, Math.round((data[i] - stats.min) * scale)));
        data[i] = data[i + 1] = data[i + 2] = value;
    }

    return imageData;
};

const boostLocalContrast = (imageData: ImageData, stats: ImageStats) => {
    const data = imageData.data;
    const midpoint = stats.average || 128;

    for (let i = 0; i < data.length; i += 4) {
        const centered = data[i] - midpoint;
        const boosted = Math.max(0, Math.min(255, Math.round(midpoint + centered * 1.45)));
        data[i] = data[i + 1] = data[i + 2] = boosted;
    }

    return imageData;
};

const buildVariants = (baseImageData: ImageData, baseStats: ImageStats) => {
    const contrastVariant = boostLocalContrast(stretchContrast(cloneImageData(baseImageData), baseStats), baseStats);
    return [baseImageData, contrastVariant];
};

const getRegionRect = (videoWidth: number, videoHeight: number, preset: ScanRegionPreset) => {
    const cropWidth = Math.max(1, Math.floor(videoWidth * preset.widthRatio));
    const cropHeight = Math.max(1, Math.floor(videoHeight * preset.heightRatio));
    const centerY = preset.centerYRatio ?? 0.5;
    const centerX = preset.centerXRatio ?? 0.5;

    const x = Math.max(0, Math.min(videoWidth - cropWidth, Math.floor(videoWidth * centerX - cropWidth / 2)));
    const y = Math.max(0, Math.min(videoHeight - cropHeight, Math.floor(videoHeight * centerY - cropHeight / 2)));

    return {
        x,
        y,
        width: cropWidth,
        height: cropHeight,
    };
};

const buildZXingHints = (autoConvertUpca: boolean) => {
    const hints = new Map();
    hints.set(
        DecodeHintType.POSSIBLE_FORMATS,
        autoConvertUpca ? ZXING_FORMATS.filter((format) => format !== BarcodeFormat.UPC_E) : ZXING_FORMATS,
    );
    hints.set(DecodeHintType.TRY_HARDER, true);
    return hints;
};

const CameraView: React.FC<CameraViewProps> = ({
    cameraEnabled,
    onBarcodeDetected,
    cameraStreamRef,
    autoConvertUpca,
    selectedDeviceId,
    onCameraReady,
}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const processedCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const zxingCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const [lastScanInfo, setLastScanInfo] = useState<ScanSuccessInfo | null>(null);
    const [debugUnlocked, setDebugUnlocked] = useState(false);

    const scanTimerIdRef = useRef<number | null>(null);
    const scanProfileIndexRef = useRef(0);
    const trackedRegionRef = useRef<TrackedRegion | null>(null);
    const debugTapRef = useRef({ count: 0, at: 0 });

    const scannerRef = useRef<ZBarScanner | null>(null);
    const zxingReaderRef = useRef<MultiFormatReader | null>(null);
    const scanningRef = useRef(false);
    const canvasSizeRef = useRef({ width: 0, height: 0 });

    const lastDetectedRef = useRef<{ value: string; at: number }>({ value: '', at: 0 });
    const scannerConfigKeyRef = useRef<string>('');
    const zxingConfigKeyRef = useRef<string>('');

    const stopScanLoop = useCallback(() => {
        if (scanTimerIdRef.current) {
            window.clearInterval(scanTimerIdRef.current);
            scanTimerIdRef.current = null;
        }
        scanningRef.current = false;
    }, []);

    const clearOverlayRects = useCallback(() => {
        const overlayCanvas = overlayCanvasRef.current;
        if (!overlayCanvas) {
            return;
        }

        const ctx = overlayCanvas.getContext('2d');
        if (!ctx) {
            return;
        }

        ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    }, []);

    // ✅ 建議 deps = []（用到的是 ref.current，不需要依賴 prop ref identity）
    const stopCameraTracks = useCallback(() => {
        if (cameraStreamRef.current) {
            cameraStreamRef.current.getTracks().forEach((t) => t.stop());
            cameraStreamRef.current = null;
        }
        if (videoRef.current) {
            try {
                videoRef.current.pause();
            } catch {
                // ignore
            }
            videoRef.current.srcObject = null;
        }
        canvasSizeRef.current = { width: 0, height: 0 };
        scanProfileIndexRef.current = 0;
        clearOverlayRects();
    }, [cameraStreamRef, clearOverlayRects]);

    const ensureScanner = useCallback(async () => {
        if (!scannerRef.current) {
            scannerRef.current = await ZBarScanner.create();
            scannerConfigKeyRef.current = '';
        }

        const scanner = scannerRef.current;
        if (!scanner) return null;

        const key = autoConvertUpca ? 'UPCA' : 'UPCE';
        if (scannerConfigKeyRef.current !== key) {
            if (autoConvertUpca) {
                scanner.setConfig(ZBarSymbolType.ZBAR_UPCA, ZBarConfigType.ZBAR_CFG_ENABLE, 1);
                scanner.setConfig(ZBarSymbolType.ZBAR_UPCE, ZBarConfigType.ZBAR_CFG_ENABLE, 0);
            } else {
                scanner.setConfig(ZBarSymbolType.ZBAR_UPCA, ZBarConfigType.ZBAR_CFG_ENABLE, 0);
                scanner.setConfig(ZBarSymbolType.ZBAR_UPCE, ZBarConfigType.ZBAR_CFG_ENABLE, 1);
            }
            scannerConfigKeyRef.current = key;
        }

        return scanner;
    }, [autoConvertUpca]);

    const ensureZXingReader = useCallback(() => {
        if (!zxingReaderRef.current) {
            zxingReaderRef.current = new MultiFormatReader();
            zxingConfigKeyRef.current = '';
        }

        const reader = zxingReaderRef.current;
        const key = autoConvertUpca ? 'UPCA' : 'UPCE';
        if (zxingConfigKeyRef.current !== key) {
            reader.setHints(buildZXingHints(autoConvertUpca));
            zxingConfigKeyRef.current = key;
        }

        return reader;
    }, [autoConvertUpca]);

    const ensureCanvasSize = (canvas: HTMLCanvasElement, width: number, height: number) => {
        if (canvasSizeRef.current.width === width && canvasSizeRef.current.height === height) {
            return;
        }

        canvas.width = width;
        canvas.height = height;
        canvasSizeRef.current = { width, height };
    };

    const ensureZXingCanvas = useCallback((width: number, height: number) => {
        if (!zxingCanvasRef.current) {
            zxingCanvasRef.current = document.createElement('canvas');
        }

        const canvas = zxingCanvasRef.current;
        if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width;
            canvas.height = height;
        }

        return canvas;
    }, []);

    const decodeWithZXing = useCallback(
        (imageData: ImageData) => {
            const reader = ensureZXingReader();
            const canvas = ensureZXingCanvas(imageData.width, imageData.height);
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (!ctx) {
                return '';
            }

            ctx.putImageData(imageData, 0, 0);

            try {
                const source = new HTMLCanvasElementLuminanceSource(canvas);
                const bitmap = new BinaryBitmap(new HybridBinarizer(source));
                const result = reader.decode(bitmap);
                return result.getText();
            } catch (error) {
                if (!(error instanceof NotFoundException)) {
                    //console.warn('ZXing fallback decode failed:', error);
                }
                return '';
            } finally {
                reader.reset();
            }
        },
        [ensureZXingCanvas, ensureZXingReader],
    );

    const drawOverlayRects = useCallback((rects: OverlayRect[]) => {
        const overlayCanvas = overlayCanvasRef.current;
        const video = videoRef.current;
        if (!overlayCanvas || !video) {
            return;
        }

        const overlayWidth = video.clientWidth;
        const overlayHeight = video.clientHeight;
        if (!overlayWidth || !overlayHeight || !video.videoWidth || !video.videoHeight) {
            return;
        }

        if (overlayCanvas.width !== overlayWidth || overlayCanvas.height !== overlayHeight) {
            overlayCanvas.width = overlayWidth;
            overlayCanvas.height = overlayHeight;
        }

        const ctx = overlayCanvas.getContext('2d');
        if (!ctx) {
            return;
        }

        ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);

        const scaleX = overlayCanvas.width / video.videoWidth;
        const scaleY = overlayCanvas.height / video.videoHeight;

        for (const rect of rects) {
            ctx.strokeStyle = rect.strokeStyle;
            ctx.lineWidth = rect.lineWidth;
            ctx.strokeRect(rect.x * scaleX, rect.y * scaleY, rect.width * scaleX, rect.height * scaleY);
        }
    }, []);

    const handleCameraBoxTap = useCallback(() => {
        if (debugUnlocked) {
            return;
        }

        const now = Date.now();
        const tap = debugTapRef.current;
        const isConsecutive = now - tap.at <= DEBUG_TAP_INTERVAL_MS;
        const nextCount = isConsecutive ? tap.count + 1 : 1;

        debugTapRef.current = {
            count: nextCount,
            at: now,
        };

        if (nextCount >= DEBUG_UNLOCK_TAPS) {
            setDebugUnlocked(true);
        }
    }, [debugUnlocked]);

    const setTrackedRegion = useCallback((region: Omit<TrackedRegion, 'expiresAt'>) => {
        trackedRegionRef.current = {
            ...region,
            expiresAt: Date.now() + TRACKED_REGION_TTL_MS,
        };
    }, []);

    const tryImproveTrackCapabilities = useCallback(
        async (stream: MediaStream) => {
            const [videoTrack] = stream.getVideoTracks();
            clearOverlayRects();
            if (!videoTrack || typeof videoTrack.getCapabilities !== 'function') {
                return;
            }

            try {
                const capabilities = videoTrack.getCapabilities() as ExtendedMediaTrackCapabilities;
                const advanced: ExtendedMediaTrackConstraintSet[] = [];

                if (capabilities.width?.max && capabilities.height?.max) {
                    advanced.push({
                        width: capabilities.width.max,
                        height: capabilities.height.max,
                    });
                }

                if (capabilities.focusMode?.includes('continuous')) {
                    advanced.push({ focusMode: 'continuous' });
                }

                if (capabilities.resizeMode?.includes('crop-and-scale')) {
                    advanced.push({ resizeMode: 'crop-and-scale' });
                }

                if (capabilities.zoom) {
                    const zoomMin = capabilities.zoom.min ?? 1;
                    const zoomMax = capabilities.zoom.max ?? zoomMin;
                    const preferredZoom = Math.min(1.4, zoomMax);
                    if (preferredZoom > zoomMin) {
                        advanced.push({ zoom: preferredZoom });
                    }
                }

                if (capabilities.sharpness) {
                    const sharpnessMin = capabilities.sharpness.min ?? 0;
                    const sharpnessMax = capabilities.sharpness.max ?? sharpnessMin;
                    const preferredSharpness = Math.min(
                        sharpnessMax,
                        Math.max(sharpnessMin, sharpnessMin + (sharpnessMax - sharpnessMin) * 0.7),
                    );
                    advanced.push({ sharpness: preferredSharpness });
                }

                if (advanced.length > 0) {
                    await videoTrack.applyConstraints({ advanced } as ExtendedMediaTrackConstraints);
                }
            } catch (error) {
                console.warn('Unable to apply camera capability enhancements:', error);
            }
        },
        [clearOverlayRects],
    );

    // =========================
    // Camera open/close
    // =========================
    useEffect(() => {
        const abortController = new AbortController();

        const startCamera = async () => {
            if (!cameraEnabled || !videoRef.current) return;

            try {
                const videoConstraints: ExtendedMediaTrackConstraints = {
                    width: { ideal: CAMERA_BOOTSTRAP_WIDTH },
                    height: { ideal: CAMERA_BOOTSTRAP_HEIGHT },
                    frameRate: { ideal: CAMERA_MAX_FRAME_RATE, max: CAMERA_MAX_FRAME_RATE },
                    facingMode: 'environment',
                };

                if (selectedDeviceId) {
                    //console.log('selectedDeviceId', selectedDeviceId);
                    videoConstraints.deviceId = { exact: selectedDeviceId };
                }
                //console.log('videoConstraints', videoConstraints);
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: videoConstraints,
                    audio: false,
                });

                if (abortController.signal.aborted) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }

                await tryImproveTrackCapabilities(stream);

                if (videoRef.current.srcObject !== stream) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                }

                cameraStreamRef.current = stream;
                onCameraReady?.();
            } catch (error) {
                if (abortController.signal.aborted) return;
                console.error('Error accessing the camera:', error);
            }
        };

        if (cameraEnabled) {
            startCamera();
        } else {
            stopScanLoop();
            stopCameraTracks();
        }

        return () => {
            abortController.abort();
            stopScanLoop();
            stopCameraTracks();
        };
    }, [cameraEnabled, cameraStreamRef, selectedDeviceId, stopCameraTracks, stopScanLoop, tryImproveTrackCapabilities]);

    useEffect(() => {
        if (!cameraEnabled) {
            setLastScanInfo(null);
        }
    }, [cameraEnabled]);

    // =========================
    // Scan loop (fixed FPS)
    // =========================
    useEffect(() => {
        if (!cameraEnabled) return;
        if (!videoRef.current || !processedCanvasRef.current) return;

        const video = videoRef.current;
        const canvas = processedCanvasRef.current;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        if (!ctx) return;

        ctx.imageSmoothingEnabled = false;

        let stopped = false;

        const shouldEmit = (value: string) => {
            const now = Date.now();
            const last = lastDetectedRef.current;
            if (value === last.value && now - last.at < DUP_TTL_MS) return false;
            lastDetectedRef.current = { value, at: now };
            return true;
        };

        const scanRect = async (
            scanner: ZBarScanner,
            region: OverlayRect,
            maxVariants = 2,
            strokeStyle = 'rgba(255, 193, 7, 0.95)',
            metadata?: {
                widthRatio: number | null;
                heightRatio: number | null;
                outputScale: number | null;
                tracked: boolean;
            },
        ) => {
            if (!video.videoWidth || !video.videoHeight) {
                return null;
            }

            const targetWidth = Math.max(1, Math.min(MAX_SCAN_WIDTH, Math.floor(region.width)));
            const targetHeight = Math.max(1, Math.min(MAX_SCAN_HEIGHT, Math.floor(region.height)));

            ensureCanvasSize(canvas, targetWidth, targetHeight);
            ctx.drawImage(video, region.x, region.y, region.width, region.height, 0, 0, targetWidth, targetHeight);

            const baseImageData = ctx.getImageData(0, 0, targetWidth, targetHeight);
            const baseStats = toGrayscale(baseImageData);
            const overlayRects: OverlayRect[] = [
                {
                    ...region,
                    strokeStyle,
                    lineWidth: 2,
                },
            ];

            drawOverlayRects(overlayRects);

            const variants = buildVariants(baseImageData, baseStats).slice(0, maxVariants);
            for (let variantIndex = 0; variantIndex < variants.length; variantIndex += 1) {
                const variant = variants[variantIndex];
                const symbols = await scanImageData(variant, scanner);
                if (symbols.length > 0) {
                    setTrackedRegion({ ...region, score: 1 });
                    return {
                        code: symbols[0].decode(),
                        decoder: 'zbar' as const,
                        roiX: Math.round(region.x),
                        roiY: Math.round(region.y),
                        roiWidth: Math.round(region.width),
                        roiHeight: Math.round(region.height),
                        outputWidth: targetWidth,
                        outputHeight: targetHeight,
                        widthRatio: metadata?.widthRatio ?? null,
                        heightRatio: metadata?.heightRatio ?? null,
                        outputScale: metadata?.outputScale ?? null,
                        variantIndex,
                        tracked: metadata?.tracked ?? false,
                    } satisfies ScanSuccessInfo;
                }
            }

            const fallbackCode = decodeWithZXing(variants[0]);
            if (fallbackCode) {
                setTrackedRegion({ ...region, score: 1 });
                return {
                    code: fallbackCode,
                    decoder: 'zxing' as const,
                    roiX: Math.round(region.x),
                    roiY: Math.round(region.y),
                    roiWidth: Math.round(region.width),
                    roiHeight: Math.round(region.height),
                    outputWidth: targetWidth,
                    outputHeight: targetHeight,
                    widthRatio: metadata?.widthRatio ?? null,
                    heightRatio: metadata?.heightRatio ?? null,
                    outputScale: metadata?.outputScale ?? null,
                    variantIndex: 0,
                    tracked: metadata?.tracked ?? false,
                } satisfies ScanSuccessInfo;
            }

            return null;
        };

        const scanRegion = async (scanner: ZBarScanner, preset: ScanRegionPreset) => {
            if (!video.videoWidth || !video.videoHeight) {
                return null;
            }

            const region = getRegionRect(video.videoWidth, video.videoHeight, preset);
            const scaledWidth = Math.max(1, Math.floor(region.width * (preset.outputScale ?? 1)));
            const scaledHeight = Math.max(1, Math.floor(region.height * (preset.outputScale ?? 1)));
            const scaledX = Math.max(
                0,
                Math.min(video.videoWidth - scaledWidth, Math.floor(region.x - (scaledWidth - region.width) / 2)),
            );
            const scaledY = Math.max(
                0,
                Math.min(video.videoHeight - scaledHeight, Math.floor(region.y - (scaledHeight - region.height) / 2)),
            );
            const scaledRegion: OverlayRect = {
                x: scaledX,
                y: scaledY,
                width: scaledWidth,
                height: scaledHeight,
                strokeStyle: 'rgba(255, 193, 7, 0.95)',
                lineWidth: 2,
            };

            return scanRect(scanner, scaledRegion, preset.maxVariants ?? 2, 'rgba(255, 193, 7, 0.95)', {
                widthRatio: preset.widthRatio,
                heightRatio: preset.heightRatio,
                outputScale: preset.outputScale ?? 1,
                tracked: false,
            });
        };

        const tick = async () => {
            if (stopped) return;
            if (scanningRef.current) return;

            if (!video.videoWidth || !video.videoHeight) return;

            try {
                scanningRef.current = true;

                const scanner = await ensureScanner();
                if (!scanner) return;

                const trackedRegion = trackedRegionRef.current;
                if (trackedRegion && trackedRegion.expiresAt > Date.now()) {
                    const trackedResult = await scanRect(
                        scanner,
                        {
                            x: trackedRegion.x,
                            y: trackedRegion.y,
                            width: trackedRegion.width,
                            height: trackedRegion.height,
                            strokeStyle: 'rgba(33, 150, 243, 0.95)',
                            lineWidth: 2,
                        },
                        2,
                        'rgba(33, 150, 243, 0.95)',
                        {
                            widthRatio: null,
                            heightRatio: null,
                            outputScale: null,
                            tracked: true,
                        },
                    );

                    if (trackedResult) {
                        setLastScanInfo(trackedResult);
                        if (shouldEmit(trackedResult.code)) {
                            onBarcodeDetected(trackedResult.code);
                        }
                        return;
                    }
                } else {
                    trackedRegionRef.current = null;
                }

                const profile = SCAN_PROFILES[scanProfileIndexRef.current] ?? SCAN_PROFILES[0];
                scanProfileIndexRef.current = (scanProfileIndexRef.current + 1) % SCAN_PROFILES.length;

                for (const preset of profile) {
                    const result = await scanRegion(scanner, preset);
                    if (!result) {
                        continue;
                    }

                    setLastScanInfo(result);
                    if (shouldEmit(result.code)) {
                        onBarcodeDetected(result.code);
                    }

                    return;
                }
            } catch (error) {
                console.error('Error scanning barcode:', error);
            } finally {
                scanningRef.current = false;
            }
        };

        stopScanLoop();
        scanTimerIdRef.current = window.setInterval(() => {
            void tick();
        }, SCAN_INTERVAL_MS);

        return () => {
            stopped = true;
            stopScanLoop();
            clearOverlayRects();
        };
    }, [
        cameraEnabled,
        clearOverlayRects,
        decodeWithZXing,
        drawOverlayRects,
        ensureScanner,
        onBarcodeDetected,
        setTrackedRegion,
        stopScanLoop,
        selectedDeviceId,
    ]);

    // =========================
    // Destroy scanner when unmount
    // =========================
    useEffect(() => {
        return () => {
            stopScanLoop();
            stopCameraTracks();

            if (scannerRef.current) {
                try {
                    scannerRef.current.destroy();
                } catch {
                    // ignore
                }
                scannerRef.current = null;
            }

            clearOverlayRects();
        };
    }, [clearOverlayRects, stopCameraTracks, stopScanLoop]);

    return (
        <>
            <canvas ref={processedCanvasRef} style={{ display: 'none' }} />

            <div className="camera-box" style={{ position: 'relative' }} onClick={handleCameraBoxTap}>
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    style={{
                        width: '100%',
                        height: '100%',
                        transform: 'scale(1.3)',
                        transformOrigin: 'center',
                    }}
                />
                <canvas
                    ref={overlayCanvasRef}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        transform: 'scale(1.3)',
                        transformOrigin: 'center',
                    }}
                />
            </div>

            {debugUnlocked && lastScanInfo && (
                <div
                    style={{
                        marginTop: 12,
                        padding: 12,
                        borderRadius: 8,
                        border: '1px solid rgba(255, 255, 255, 0.18)',
                        background: 'rgba(0, 0, 0, 0.45)',
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: 13,
                        lineHeight: 1.5,
                    }}
                >
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Last successful scan</div>
                    <div>Code: {lastScanInfo.code}</div>
                    <div>Decoder: {lastScanInfo.decoder}</div>
                    <div>Tracked ROI: {lastScanInfo.tracked ? 'yes' : 'no'}</div>
                    <div>
                        ROI: {lastScanInfo.roiWidth} x {lastScanInfo.roiHeight} at ({lastScanInfo.roiX},{' '}
                        {lastScanInfo.roiY})
                    </div>
                    <div>
                        Output window: {lastScanInfo.outputWidth} x {lastScanInfo.outputHeight}
                    </div>
                    <div>
                        Ratios: w {lastScanInfo.widthRatio ?? '-'} / h {lastScanInfo.heightRatio ?? '-'} / scale{' '}
                        {lastScanInfo.outputScale ?? '-'}
                    </div>
                    <div>Variant index: {lastScanInfo.variantIndex}</div>
                </div>
            )}
        </>
    );
};

export default CameraView;
